import * as React from 'react';

import {
    Button,
    Dialog,
    DialogActions,
    DialogContentText,
    FormControlLabel,
    Switch,
    TextField,
    Theme,
    createStyles,
    withStyles,
} from '@material-ui/core';
import { inject, observer } from 'mobx-react';

import { AchivementsRequest } from '../../services/api/AchievementsRequest';
import AppIcon from '../shared/AppIcon';
import { AppIconType } from '../shared/AppIconType';
import { ClientTelemetryEvent } from '../../services/tx/ClientTelemetryEvent';
import { CloseableDialogTitle } from '../shared/CloseableDialog';
import CopyToClipboard from 'react-copy-to-clipboard';
import { IConfigStoreProps } from '../../stores/IConfigStoreProps';
import { IContextStoreProps } from '../../stores/IContextStoreProps';
import { INotificationStoreProps } from '../../stores/INotificationStoreProps';
import MuiDialogContent from '@material-ui/core/DialogContent';
import { PredefinedAchievementCheckpoint } from '../../services/api/PredefinedAchievementCheckpoint';
import { PredefinedAchievementScenario } from '../../services/api/PredefinedAchievementScenario';
import { PredefinedAuthProvider } from '../../domain/auth/PredefinedAuthProvider';
import { PredefinedStoreName } from '../enums/PredefinedStoreName';
import achivementsService from '../../services/api/AchievementsService';
import i18n from '../../services/localization/i18n';
import { isTeamsAddonMode } from '../../utils';
import telemetryDataService from '../../services/tx/TelemetryDataService';

const styles = (theme: Theme) =>
    createStyles({
        actions: {},
        closeButton: {
            color: theme.palette.grey[500],
            position: 'absolute',
            right: theme.spacing(),
            top: theme.spacing(),
        },
        dialog: {},
        dialogContent: {
            minWidth: 500,
        },
        leftSmallIcon: {
            fontSize: 20,
            marginRight: theme.spacing(),
        },
        legend: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginRight: theme.spacing(),
        },
        passcode: {
            display: 'flex',
            flexDirection: 'column',
        },
        root: {
            paddingBottom: 0,
        },
        switch: {
            width: 'fit-content',
        },
        text: {
            minHeight: 96,
            width: '100%',
        },
        textField: {
            width: '100%',
        },
    });

const DialogContent = withStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

interface IShareInviteProps {
    classes?: {
        actions: string;
        closeButton: string;
        dialog: string;
        dialogContent: string;
        leftSmallIcon: string;
        legend: string;
        passcode: string;
        root: string;
        switch: string;
        text: string;
        textField: string;
    };
    open: boolean;
    onClose: () => void;
}

interface IShareInviteState {
    appendPasscode: boolean;
    urlCopied: boolean;
    passcode?: string;
    passcodeCopied: boolean;
    url: string;
}

@inject(PredefinedStoreName.CONFIG)
@inject(PredefinedStoreName.CONTEXT)
@inject(PredefinedStoreName.NOTIFICATION)
@observer
class ShareInvite extends React.Component<
    IShareInviteProps &
        IConfigStoreProps &
        IContextStoreProps &
        INotificationStoreProps,
    IShareInviteState
> {
    private copyUrlTimer?: number;
    private copyTextTimer?: number;

    /**
     * Creates an instance of ShareInvite.
     * @param {(IShareInviteProps & IConfigStoreProps & IContextStoreProps)} props
     * @memberof ShareInvite
     */
    constructor(
        props: IShareInviteProps &
            IConfigStoreProps &
            IContextStoreProps &
            INotificationStoreProps
    ) {
        super(props);

        const { configStore, contextStore } = this.props;

        const { acls } = this.props.contextStore!;
        let passcodeAcl;
        if (acls) {
            passcodeAcl = acls.find(
                (acl) => acl.provider === PredefinedAuthProvider.Passcode
            );
        }

        this.state = {
            appendPasscode: true,
            passcode: passcodeAcl?.condition,
            passcodeCopied: false,
            url: `${configStore!.hosts.web}/${contextStore!.resourceId}`,
            urlCopied: false,
        };
    }

    public render() {
        const { classes } = this.props;

        return (
            <Dialog
                open={this.props.open}
                onClose={this.props.onClose}
                aria-labelledby="form-dialog-title"
                className={classes!.dialog}
            >
                <CloseableDialogTitle
                    id="form-dialog-title"
                    onClose={this.props.onClose}
                >
                    {i18n.t('invite_title_text')}
                </CloseableDialogTitle>

                <DialogContent className={classes!.dialogContent}>
                    <DialogContentText>
                        {i18n.t('invite_legend_text')}
                    </DialogContentText>
                    {this.state.urlCopied && (
                        <TextField
                            label={i18n.t('invite_link_label')}
                            defaultValue={i18n.t('invite_link_defaultCopied')}
                            disabled={true}
                            className={classes!.textField}
                            margin="normal"
                            variant="filled"
                            InputProps={{
                                color: 'secondary',
                                readOnly: true,
                            }}
                        />
                    )}
                    {!this.state.urlCopied && (
                        <TextField
                            autoFocus={true}
                            label={i18n.t('invite_link_label')}
                            value={this.getMeetUrl()}
                            className={classes!.textField}
                            margin="normal"
                            variant="outlined"
                            InputLabelProps={{
                                color: 'secondary',
                            }}
                            InputProps={{
                                color: 'secondary',
                                readOnly: true,
                            }}
                            // tslint:disable-next-line: jsx-no-lambda
                            onFocus={(event) => event.target.select()}
                        />
                    )}

                    {this.renderPasscode()}
                </DialogContent>

                <DialogActions className={classes!.legend}>
                    <div>
                        <Button
                            variant="text"
                            onClick={this.handleEmailInvite}
                            style={{
                                textTransform: 'none',
                            }}
                        >
                            <AppIcon
                                icon={AppIconType.EmailOutline}
                                className={classes!.leftSmallIcon}
                            />
                            {i18n.t('invite_link_email')}
                        </Button>
                        <CopyToClipboard
                            text={this.getMeetInvite()}
                            // tslint:disable-next-line: jsx-no-lambda
                            onCopy={this.onCopyInvite}
                        >
                            <Button
                                variant="text"
                                style={{
                                    textTransform: 'none',
                                }}
                            >
                                <AppIcon
                                    icon={AppIconType.Documents}
                                    className={classes!.leftSmallIcon}
                                />
                                {i18n.t('invite_text_copy')}
                            </Button>
                        </CopyToClipboard>
                    </div>
                    <CopyToClipboard
                        text={this.getMeetUrl()}
                        // tslint:disable-next-line: jsx-no-lambda
                        onCopy={this.onCopyUrl}
                    >
                        <Button variant="contained" color="secondary">
                            {i18n.t('invite_link_copy')}
                        </Button>
                    </CopyToClipboard>
                </DialogActions>
            </Dialog>
        );
    }

    private handleEmailInvite = () => {
        const { me } = this.props.configStore!;

        const mailSubject = i18n.t('invite_mail_subject');
        const mailBody = i18n.t('invite_mail_body');
        const mailSignature = i18n.t('invite_mail_signature');

        let body = `mailto:?subject=${encodeURIComponent(
            mailSubject
        )}&body=${encodeURIComponent(mailBody)}%0A%0A${encodeURIComponent(
            this.getMeetUrl()
        )}`;

        if (this.state.passcode && !this.state.appendPasscode) {
            body += '%0APasscode:' + this.state.passcode;
        }

        body += '%0A%0A' + mailSignature + '%2C%0A' + me!.firstName + '%0A%0A';

        window.open(body);
    };

    private getMeetInvite = () => {
        const noPasscodeText = i18n.t('invite_text_body');
        const passcodeText = i18n.t('invite_passcode_body');

        let message = noPasscodeText;
        if (this.state.passcode) {
            message = passcodeText;
        }

        return message
            .replace('{url}', this.state.url)
            .replace('{passcode}', this.state.passcode || '');
    };

    private getMeetUrl = (noPasscode?: boolean) => {
        const { appendPasscode, passcode } = this.state;

        let url = this.state.url;
        if (appendPasscode && passcode && !noPasscode) {
            url += '?pcode=' + passcode;
        }

        return url;
    };

    private renderPasscode = () => {
        const { classes } = this.props;
        const { acls } = this.props.contextStore!;
        const passcodeAcl = acls.find(
            (acl) => acl.provider === PredefinedAuthProvider.Passcode
        );

        if (!passcodeAcl) {
            return <></>;
        }

        return (
            <div className={classes!.passcode}>
                {this.state.passcodeCopied && (
                    <TextField
                        label={i18n.t('invite_passcode_label')}
                        defaultValue={i18n.t('invite_text_copied')}
                        disabled={true}
                        className={classes!.textField}
                        margin="normal"
                        variant="filled"
                        InputProps={{
                            color: 'secondary',
                            readOnly: true,
                        }}
                    />
                )}
                {!this.state.passcodeCopied && (
                    <TextField
                        autoFocus={false}
                        label={i18n.t('invite_passcode_label')}
                        defaultValue={passcodeAcl.condition}
                        margin="normal"
                        variant="outlined"
                        InputLabelProps={{
                            color: 'secondary',
                        }}
                        InputProps={{
                            color: 'secondary',
                            readOnly: true,
                        }}
                        // tslint:disable-next-line: jsx-no-lambda
                        onFocus={(event) => event.target.select()}
                    />
                )}
                <FormControlLabel
                    control={
                        <Switch
                            checked={this.state.appendPasscode}
                            onChange={() =>
                                this.setState({
                                    appendPasscode: !this.state.appendPasscode,
                                })
                            }
                            color="secondary"
                        />
                    }
                    label={i18n.t('invite_pascode_add')}
                />
            </div>
        );
    };

    public componentWillUnmount() {
        if (this.copyUrlTimer) {
            clearTimeout(this.copyUrlTimer);
        }
        if (this.copyTextTimer) {
            clearTimeout(this.copyTextTimer);
        }
    }

    private onCopyInvite = () => {
        const { showToast } = this.props.notificationStore!;

        this.setState({
            passcodeCopied: true,
            urlCopied: true,
        });

        this.copyTextTimer = window.setTimeout(() => {
            this.setState({
                passcodeCopied: false,
                urlCopied: false,
            });
        }, 3000);

        showToast(i18n.t('invite_text_copied'), 'info');
    };

    private onCopyUrl = () => {
        const { resourceId, resourceType } = this.props.contextStore!;
        const { version, environment } = this.props.configStore!;

        this.setState({ urlCopied: true });

        this.copyUrlTimer = window.setTimeout(() => {
            this.setState({ urlCopied: false });
        }, 3000);

        const scenario = isTeamsAddonMode
            ? PredefinedAchievementScenario.TEAMS_ONBOARDING
            : PredefinedAchievementScenario.MEET_GENERAL;

        const achievement: AchivementsRequest = {
            scenario: scenario,
            checkpoint: PredefinedAchievementCheckpoint.MINIBAR_INVITE_COPY,
            resourceId: resourceId!,
            resourceType: resourceType!,
        };

        achivementsService.postAsync(achievement);

        const txEvent = {
            correlationId: 'N/A',
            environment: environment,
            name: 'spa_meetApp_inviteCopy',
            sentryEventId: 'N/A',
            silent: false,
            source: 'meetApp',
            success: true,
            timeToComplete: 0,
            version: version,
        } as ClientTelemetryEvent;
        telemetryDataService.reportAsync(txEvent);
    };
}

export default withStyles(styles, { withTheme: true })(ShareInvite);
